import { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import type { PublicEvent, BroadcastColorMode, BroadcastBlinkInterval, BroadcastHoldDuration } from '@arenapulse/shared';
import { ApiClient } from '../lib/api-client';
import { getErrorMessage } from '../lib/error-messages';
import { isDebugMode, logBroadcastTiming } from '../lib/perf-debug';

interface LogEntry {
  id: string;
  timestamp: string;
  message: string;
}

export function ArenaEnginePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [event, setEvent] = useState<PublicEvent | null>(location.state?.event || null);
  const [isFetchingEvent, setIsFetchingEvent] = useState(!location.state?.event);

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLifecycleLoading, setIsLifecycleLoading] = useState(false);
  const [connectedCount, setConnectedCount] = useState<number>(0);

  const [colorMode, setColorMode] = useState<BroadcastColorMode>('solid');
  const [blinkIntervalMs, setBlinkIntervalMs] = useState<BroadcastBlinkInterval>(200);
  const [holdDuration, setHoldDuration] = useState<BroadcastHoldDuration>('continuous');

  const lastBroadcastAt = useRef(0);

  useEffect(() => {
    if (!event && isFetchingEvent && eventId) {
      const fetchEvent = async () => {
        const response = await ApiClient.getEvents();
        if (response.success) {
          const foundEvent = response.data.find((e) => e.id === eventId);
          if (foundEvent) {
            setEvent(foundEvent);
          }
        }
        setIsFetchingEvent(false);
      };
      fetchEvent();
    }
  }, [event, isFetchingEvent, eventId]);

  useEffect(() => {
    if (!eventId) return;

    let pollInterval: number;

    const fetchPresence = async () => {
      const response = await ApiClient.getEventPresence(eventId);
      if (response.success) {
        setConnectedCount(response.data.count);
      }
    };

    if (event?.status === 'live' || event?.status === 'paused') {
      fetchPresence();
      pollInterval = window.setInterval(fetchPresence, 10000);
    }

    return () => {
      window.clearInterval(pollInterval);
    };
  }, [eventId, event?.status]);

  if (isFetchingEvent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted">Loading event...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <div className="text-foreground text-lg">Event not found.</div>
        <button
          onClick={() => navigate('/')}
          className="bg-indigo-600 text-foreground px-4 py-2 rounded shadow hover:bg-indigo-700"
        >
          Back to Events
        </button>
      </div>
    );
  }

  const isLive = event.status === 'live';
  const isDraft = event.status === 'draft';
  const isPaused = event.status === 'paused';

  const addLog = (message: string) => {
    const entry: LogEntry = {
      id: crypto.randomUUID(),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      message,
    };
    setLogs((prev) => [entry, ...prev]);
  };

  const handleLifecycle = async (action: 'start' | 'pause' | 'resume' | 'end') => {
    setIsLifecycleLoading(true);
    setError(null);

    let response;
    switch (action) {
      case 'start': response = await ApiClient.startEvent(event.id); break;
      case 'pause': response = await ApiClient.pauseEvent(event.id); break;
      case 'resume': response = await ApiClient.resumeEvent(event.id); break;
      case 'end': response = await ApiClient.endEvent(event.id); break;
    }

    if (response.success) {
      setEvent(response.data);
      addLog(`Event ${action}ed`);
    } else {
      setError(getErrorMessage(response.error.code));
    }
    setIsLifecycleLoading(false);
  };

  const handleBroadcastColor = async (color: string) => {
    const clickTime = Date.now();
    if (clickTime - lastBroadcastAt.current < 150) return;
    lastBroadcastAt.current = clickTime;

    setError(null);
    
    // Fire API call without blocking
    ApiClient.broadcastColor(event.id, color, colorMode, blinkIntervalMs, holdDuration).then(response => {
      const responseTime = Date.now();
      if (isDebugMode()) {
        const label = `Color:${color}${response.success ? '' : ' (failed)'}`;
        logBroadcastTiming(label, clickTime, responseTime);
      }
      if (response.success) {
        const desc = colorMode === 'blink'
          ? `blink ${blinkIntervalMs}ms, ${holdDuration === 'continuous' ? 'continuous' : holdDuration + 's'}`
          : `solid, ${holdDuration === 'continuous' ? 'continuous' : holdDuration + 's'}`;
        addLog(`Color: ${color} (${desc})`);
      } else {
        setError(getErrorMessage(response.error.code));
      }
    });
  };

  const handleBroadcastEmoji = async (emoji: string) => {
    const clickTime = Date.now();
    if (clickTime - lastBroadcastAt.current < 150) return;
    lastBroadcastAt.current = clickTime;

    setError(null);
    
    // Fire API call without blocking
    ApiClient.broadcastEmoji(event.id, emoji).then(response => {
      const responseTime = Date.now();
      if (isDebugMode()) {
        const label = `Emoji:${emoji}${response.success ? '' : ' (failed)'}`;
        logBroadcastTiming(label, clickTime, responseTime);
      }
      if (response.success) {
        addLog(`Emoji: ${emoji}`);
      } else {
        setError(getErrorMessage(response.error.code));
      }
    });
  };

  const handleBroadcastCountdown = async (seconds: number) => {
    const clickTime = Date.now();
    if (clickTime - lastBroadcastAt.current < 150) return;
    lastBroadcastAt.current = clickTime;

    setError(null);
    
    // Fire API call without blocking
    ApiClient.broadcastCountdown(event.id, seconds).then(response => {
      const responseTime = Date.now();
      if (isDebugMode()) {
        const label = `Countdown:${seconds}s${response.success ? '' : ' (failed)'}`;
        logBroadcastTiming(label, clickTime, responseTime);
      }
      if (response.success) {
        addLog(`Countdown: ${seconds}s`);
      } else {
        setError(getErrorMessage(response.error.code));
      }
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="bg-surface shadow shrink-0">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-muted hover:text-foreground focus:outline-none"
            >
              &larr; Back
            </button>
            <h1 className="text-xl font-bold text-foreground">{event.name}</h1>
            <span className="inline-flex items-center rounded-md bg-border px-2 py-1 text-xs font-medium text-foreground ring-1 ring-inset ring-border uppercase">
              {event.status}
            </span>
            {(isLive || isPaused) && (
              <span className="text-sm font-medium text-muted ml-4">
                Connected Users : {connectedCount}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {(isDraft || isPaused) && (
              <button
                disabled={isLifecycleLoading}
                onClick={() => handleLifecycle(isDraft ? 'start' : 'resume')}
                className="bg-surface text-foreground px-3 py-1 rounded text-sm font-medium hover:bg-muted disabled:opacity-50"
              >
                {isDraft ? 'Start Event' : 'Resume Event'}
              </button>
            )}
            {isLive && (
              <button
                disabled={isLifecycleLoading}
                onClick={() => handleLifecycle('pause')}
                className="bg-surface text-foreground px-3 py-1 rounded text-sm font-medium hover:bg-muted disabled:opacity-50"
              >
                Pause Event
              </button>
            )}
            {(isLive || isPaused) && (
              <button
                disabled={isLifecycleLoading}
                onClick={() => handleLifecycle('end')}
                className="bg-destructive text-foreground px-3 py-1 rounded text-sm font-medium hover:bg-destructive/90 disabled:opacity-50"
              >
                End Event
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-auto mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-md bg-surface border border-destructive p-4">
            <div className="text-sm text-destructive">{error}</div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <section className="bg-surface rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-foreground">Colors</h2>
              </div>

              <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Mode toggle */}
                <div className="bg-background p-4 rounded border border-border flex flex-col">
                  <span className="text-sm font-medium text-foreground mb-4">Mode</span>
                  <div className="flex bg-surface rounded border border-border overflow-hidden mt-auto">
                    {(['blink', 'solid'] as BroadcastColorMode[]).map((mode) => (
                      <button
                        key={mode}
                        onClick={() => setColorMode(mode)}
                        className={`flex-1 py-2 text-sm font-medium transition-colors ${
                          colorMode === mode
                            ? 'bg-primary text-primary-foreground'
                            : 'text-foreground hover:bg-muted'
                        }`}
                      >
                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Blink Speed */}
                <div className={`bg-background p-4 rounded border border-border flex flex-col ${colorMode !== 'blink' ? 'opacity-50' : ''}`}>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm font-medium text-foreground">Blink Speed</span>
                  </div>
                  <div className="mt-auto">
                    <input
                      type="range"
                      min={0}
                      max={3}
                      step={1}
                      disabled={colorMode !== 'blink'}
                      value={[100, 200, 300, 500].indexOf(blinkIntervalMs)}
                      onChange={(e) => {
                        const values: BroadcastBlinkInterval[] = [100, 200, 300, 500];
                        setBlinkIntervalMs(values[Number(e.target.value)]);
                      }}
                      className="w-full accent-primary"
                    />
                    <div className="flex justify-between text-xs mt-2 px-1 text-muted">
                      {['100', '200', '300', '500'].map((label, idx) => {
                        const valueMap: BroadcastBlinkInterval[] = [100, 200, 300, 500];
                        const isSelected = blinkIntervalMs === valueMap[idx];
                        return (
                          <span key={label} className={isSelected ? 'text-primary font-bold' : ''}>
                            {label}ms
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Hold Duration */}
                <div className="bg-background p-4 rounded border border-border flex flex-col">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm font-medium text-foreground">Hold Duration</span>
                  </div>
                  <div className="mt-auto">
                    <input
                      type="range"
                      min={0}
                      max={4}
                      step={1}
                      value={[1, 2, 3, 5, 'continuous'].indexOf(holdDuration)}
                      onChange={(e) => {
                        const values: BroadcastHoldDuration[] = [1, 2, 3, 5, 'continuous'];
                        setHoldDuration(values[Number(e.target.value)]);
                      }}
                      className="w-full accent-primary"
                    />
                    <div className="flex justify-between text-xs mt-2 px-1 text-muted">
                      {['1s', '2s', '3s', '5s', '∞ Continuous'].map((label, idx) => {
                        const valueMap: BroadcastHoldDuration[] = [1, 2, 3, 5, 'continuous'];
                        const isSelected = holdDuration === valueMap[idx];
                        return (
                          <span key={label} className={isSelected ? 'text-primary font-bold' : ''}>
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {['RED', 'GREEN', 'BLUE', 'WHITE'].map((color) => (
                  <button
                    key={color}
                    disabled={!isLive}
                    onClick={() => handleBroadcastColor(color)}
                    className={`h-16 rounded shadow text-sm font-bold uppercase transition-colors disabled:opacity-50
                      ${color === 'RED' ? 'bg-surface border border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground' : ''}
                      ${color === 'GREEN' ? 'bg-surface border border-primary text-primary hover:bg-primary hover:text-primary-foreground' : ''}
                      ${color === 'BLUE' ? 'bg-surface border border-primary text-primary hover:bg-primary hover:text-primary-foreground' : ''}
                      ${color === 'WHITE' ? 'bg-surface text-foreground hover:bg-muted border border-border' : ''}
                    `}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </section>

            <section className="bg-surface rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-foreground mb-4">Emojis</h2>
              <div className="grid grid-cols-4 gap-4">
                {['😀', '❤️', '👏', '🔥', '🎉', '⭐', '👍', '👋'].map((emoji) => (
                  <button
                    key={emoji}
                    disabled={!isLive}
                    onClick={() => handleBroadcastEmoji(emoji)}
                    className="h-16 text-3xl flex items-center justify-center rounded bg-background hover:bg-surface border border-border disabled:opacity-50"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </section>

            <section className="bg-surface rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-foreground mb-4">Countdown</h2>
              <div className="grid grid-cols-5 gap-4">
                {[3, 5, 10, 30, 60].map((seconds) => (
                  <button
                    key={seconds}
                    disabled={!isLive}
                    onClick={() => handleBroadcastCountdown(seconds)}
                    className="h-12 rounded bg-primary text-primary-foreground font-semibold hover:bg-muted border border-border disabled:opacity-50"
                  >
                    {seconds}s
                  </button>
                ))}
              </div>
            </section>
          </div>

          <div className="lg:col-span-1">
            <section className="bg-surface rounded-lg shadow h-full flex flex-col">
              <div className="p-4 border-b border-border shrink-0">
                <h2 className="text-lg font-medium text-foreground">Broadcast Log</h2>
              </div>
              <div className="p-4 flex-1 overflow-auto bg-background max-h-[600px]">
                {logs.length === 0 ? (
                  <p className="text-sm text-muted text-center italic mt-4">No broadcasts yet</p>
                ) : (
                  <ul className="space-y-3">
                    {logs.map((log) => (
                      <li key={log.id} className="flex items-center text-sm gap-3">
                        <span className="text-muted shrink-0 font-mono">{log.timestamp}</span>
                        <span className="text-foreground font-medium">{log.message}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
